import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { Textarea } from "@fieldwork/ui/components/textarea";
import { cn } from "@fieldwork/ui/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { errorMessage, Field, Page, Select, toServerName } from "@/components/ui-bits";
import { authClient } from "@/lib/auth-client";
import { client, orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/servers/new")({
  component: NewServer,
});

const zones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["UTC"];
/** The smallest size the panel picks by default; smaller ones work but leave Plane little room. */
const RECOMMENDED_GB = 8;

const CONTROL = "h-11 bg-[#111111]";
const LABEL = "font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]";

const eur = (n: number | string | undefined) => `€${Number(n ?? 0).toFixed(2)}`;

function Back() {
  return (
    <Link to="/servers" className="self-start font-mono text-xs text-[#A8A59D] no-underline hover:text-[#FF4F1A]">
      ← Servers
    </Link>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-4 rounded-md border-0 bg-[#1A1A19] px-6 py-[22px] shadow-[inset_0_0_0_1px_#2A2927]">
      <legend className="float-left p-0 font-mono text-base font-bold">{title}</legend>
      {children}
    </fieldset>
  );
}

/** A radio card, as in the design's Location and Size pickers. */
function Choice({
  name,
  checked,
  onChange,
  children,
  center,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
  center?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer gap-2.5 rounded-md px-3.5",
        center ? "min-h-12 items-center" : "py-3.5",
        checked ? "bg-[#222220] shadow-[inset_0_0_0_1.5px_#FF4F1A]" : "bg-[#161615] shadow-[inset_0_0_0_1px_#34332F] hover:shadow-[inset_0_0_0_1px_#4A4843]",
      )}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className={cn("size-4 shrink-0 accent-[#FF4F1A]", !center && "mt-[3px]")}
      />
      {children}
    </label>
  );
}

function NewServer() {
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  // The Hetzner token is kept in this page only: sent to list sizes and to create the server, never stored.
  const [tokenInput, setTokenInput] = useState("");
  const [token, setToken] = useState("");
  const [form, setForm] = useState({
    name: "tasks",
    location: "",
    serverType: "",
    ownerName: "",
    ownerEmail: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    sshPublicKey: "",
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (session && !form.ownerName) setForm((f) => ({ ...f, ownerName: session.user.name, ownerEmail: session.user.email }));
  }, [session, form.ownerName]);

  const options = useQuery({
    ...orpc.credentials.options.queryOptions({ input: { token } }),
    enabled: token.length >= 20,
    retry: false,
  });
  const types = useMemo(
    () => (options.data?.serverTypes ?? []).filter((t) => !form.location || t.locations.includes(form.location)),
    [options.data, form.location],
  );
  useEffect(() => {
    if (options.data && !form.location) {
      const loc = options.data.locations.find((l) => l.name === "nbg1") ?? options.data.locations[0];
      if (loc) setForm((f) => ({ ...f, location: loc.name }));
    }
  }, [options.data, form.location]);
  useEffect(() => {
    if (types.length && !types.some((t) => t.name === form.serverType)) {
      const pick = types.find((t) => t.memory >= RECOMMENDED_GB) ?? types[0];
      if (pick) setForm((f) => ({ ...f, serverType: pick.name }));
    }
  }, [types, form.serverType]);

  const create = useMutation({
    mutationFn: () => client.servers.createHetzner({ ...form, name: form.name.replace(/-+$/, ""), token }),
    onSuccess: ({ id }) => {
      toast.success("Server created. Installing takes about 10 minutes.");
      navigate({ to: "/servers/$id", params: { id } });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const subtitle =
    "Created in your own Hetzner project, so you pay Hetzner directly. Your data lives on that server; the panel passes along what you open here.";

  const chosen = types.find((t) => t.name === form.serverType);
  const recommended = types.find((t) => t.memory >= RECOMMENDED_GB)?.name;
  const city = options.data?.locations.find((l) => l.name === form.location)?.city ?? form.location;
  const price = chosen ? eur(chosen.prices[form.location]) : null;

  return (
    <Page title="New server" subtitle={subtitle} back={<Back />}>
      <div className="flex flex-wrap items-start gap-6">
        <form
          id="new-server"
          className="flex min-w-0 flex-[2_1_560px] flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Section title="1 · Where it runs">
            <Field label="Hetzner API token (Read & Write)">
              <div className="flex flex-wrap gap-2">
                <Input
                  className={cn(CONTROL, "min-w-0 flex-[1_1_280px] font-mono")}
                  type="password"
                  autoComplete="off"
                  placeholder="Paste a token from Hetzner → Security → API tokens"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  onBlur={() => setToken(tokenInput.trim())}
                  required
                />
                <Button type="button" variant="outline" className="h-11" onClick={() => setToken(tokenInput.trim())}>
                  Load sizes
                </Button>
              </div>
            </Field>
            <p className="m-0 text-[12px] text-[#A8A59D]">
              Used to list sizes and create this server, then forgotten: Fieldwork doesn't keep cloud tokens. You can
              delete the token in Hetzner once the server is created; restarting or deleting the server later asks for one again.
            </p>
            {options.error && <p className="m-0 text-[13px] text-[#F48A80]">{options.error.message}</p>}
            {options.isLoading && <p className="m-0 text-[13px] text-[#A8A59D]">Loading locations and sizes from Hetzner…</p>}
            {options.data && (
              <>
                <div role="radiogroup" aria-label="Location" className="flex flex-col gap-1.5">
                  <span className={LABEL}>Location</span>
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-2">
                    {options.data.locations.map((l) => (
                      <Choice
                        key={l.name}
                        name="location"
                        center
                        checked={form.location === l.name}
                        onChange={() => setForm({ ...form, location: l.name })}
                      >
                        {l.city}
                      </Choice>
                    ))}
                  </div>
                </div>
                <div role="radiogroup" aria-label="Size" className="flex flex-col gap-1.5">
                  <span className={LABEL}>Size</span>
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-2">
                    {types.map((t) => (
                      <Choice
                        key={t.name}
                        name="size"
                        checked={form.serverType === t.name}
                        onChange={() => setForm({ ...form, serverType: t.name })}
                      >
                        <span className="flex flex-col gap-0.5">
                          <span className="font-mono font-bold">
                            {t.name}{" "}
                            {t.name === recommended && (
                              <span className="text-[11px] font-medium text-[#FF8A63]">recommended</span>
                            )}
                          </span>
                          <span className="text-[13px] text-[#A8A59D]">
                            {t.cores} vCPU · {t.memory} GB · {t.disk} GB
                          </span>
                          <span className="text-[13px]">{eur(t.prices[form.location])} / month</span>
                          {t.memory < RECOMMENDED_GB && <span className="text-xs text-[#F0B65A]">tight for Plane</span>}
                        </span>
                      </Choice>
                    ))}
                  </div>
                  <span className="text-xs text-[#A8A59D]">Prices come from your Hetzner account, incl. VAT, billed by Hetzner.</span>
                </div>
              </>
            )}
          </Section>

          <Section title="2 · Who it works for">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-4">
              <Field label="Server name" hint="Lowercase letters, digits and dashes">
                <Input
                  className={cn(CONTROL, "font-mono")}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: toServerName(e.target.value) })}
                  onBlur={() => setForm({ ...form, name: form.name.replace(/-+$/, "") })}
                  pattern="[a-z0-9]([a-z0-9\-]*[a-z0-9])?"
                  required
                />
              </Field>
              <Field label="Timezone">
                <Select className={CONTROL} value={form.timezone} onChange={set("timezone")}>
                  {zones.map((z) => (
                    <option key={z}>{z}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Your name" hint="Fieldwork works for this person">
                <Input className={CONTROL} value={form.ownerName} onChange={set("ownerName")} required />
              </Field>
              <Field label="Your email" hint="Becomes your Plane login">
                <Input className={CONTROL} type="email" value={form.ownerEmail} onChange={set("ownerEmail")} required />
              </Field>
            </div>
          </Section>

          <details className="rounded-md bg-[#1A1A19] px-6 py-[18px] shadow-[inset_0_0_0_1px_#2A2927]">
            <summary className="cursor-pointer font-mono text-sm font-bold">
              3 · Emergency access <span className="font-normal text-[#A8A59D]">(optional)</span>
            </summary>
            <div className="mt-3.5">
              <Field label="SSH public key" hint="The firewall blocks SSH until you open port 22 in the Hetzner console.">
                <Textarea
                  rows={2}
                  className="bg-[#111111] font-mono text-[13px]"
                  value={form.sshPublicKey}
                  onChange={set("sshPublicKey")}
                  placeholder="ssh-ed25519 AAAA… you@laptop"
                />
              </Field>
            </div>
          </details>
        </form>

        <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-4 rounded-md bg-[#1A1A19] px-6 py-[22px] shadow-[inset_0_0_0_1px_#3A3935]">
          <h2 className="m-0 font-mono text-base font-bold">Summary</h2>
          <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-[13px]">
            <dt className="text-[#A8A59D]">Server</dt>
            <dd className="m-0 font-mono">{form.name || "–"}</dd>
            <dt className="text-[#A8A59D]">Size</dt>
            <dd className="m-0">{chosen ? `${chosen.name} · ${city}` : "–"}</dd>
            <dt className="text-[#A8A59D]">Cost</dt>
            <dd className="m-0">{price ? `${price} / month, billed by Hetzner` : "–"}</dd>
          </dl>
          <ol className="m-0 flex flex-col gap-1.5 pl-[18px] text-[13px] text-[#CFCBC1]">
            <li>Created with a firewall that allows no inbound connections</li>
            <li>Installs Fieldwork and Plane, about 10 minutes</li>
            <li>Connects out to this panel; then you connect your sources</li>
          </ol>
          <Button type="submit" form="new-server" size="lg" className="h-12 text-sm" disabled={create.isPending || !form.serverType}>
            {create.isPending ? "Creating…" : "Create server"}
          </Button>
        </aside>
      </div>
    </Page>
  );
}
